import {
  Box,
  CollapsibleCard,
  ConfirmationDialogBox,
  DatePicker,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFoot as TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { SelectInput } from '@components';
import { userRoles } from '@constants/common';
import {
  customerDetailsStore,
  updateCustomerDetailsState,
} from '@store/customers/customerDetails';
import { checkIfDialogBoxIsOpen, closeDialogBox } from '@store/dialogBox';
import { userStore } from '@store/user';
import { PlusCircleOutlineIcon, DeleteIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { intersection } from 'lodash';
import { DateTime } from 'luxon';
import { createSignal, Index, Show } from 'solid-js';

import { CustomerPolicyType } from '../../constants';
import { insuranceClasses as classes } from './classes';

const Insurance = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteRow, setDeleteRow] = createSignal<number | null>(null);

  const headers = ['Coverage Amount', 'Policy Type', 'Policy Date', ''];

  const isReadonly = () => {
    return (
      intersection(userStore.user.roles, [
        userRoles.ADMIN,
        userRoles.ADMIN_LITE,
      ]).length <= 0
    );
  };

  if (isReadonly()) {
    headers.pop();
  }

  const addRow = () => {
    const customerInsuranceItems = [
      ...(customerDetailsStore.customer.insurance || []),
    ];
    customerInsuranceItems.push({
      operationType: 'Insert',
      objectId: customerDetailsStore.customer.id as number,
      objectType: 'Customer',
      policyType: '',
      policyTypeName: '',
      policyDate: '',
      coverageAmount: 0,
    });
    updateCustomerDetailsState({ insurance: customerInsuranceItems });
  };

  const updateInsuranceItems = (
    key: string,
    value: string | number,
    index: number,
  ) => {
    const insuranceItems = [...(customerDetailsStore.customer.insurance || [])];
    const { operationType } = insuranceItems[index];
    if (operationType !== 'Insert') {
      insuranceItems[index] = {
        ...insuranceItems[index],
        operationType: 'Update',
        [key]: value,
      };
    } else {
      insuranceItems[index] = {
        ...insuranceItems[index],
        [key]: value,
      };
    }
    updateCustomerDetailsState({ insurance: insuranceItems });
  };

  const handleDeleteInsurance = (index: number) => {
    const insuranceItems = [...(customerDetailsStore.customer.insurance || [])];
    const item = insuranceItems[index];
    const { operationType } = insuranceItems[index];

    if (operationType === 'Insert') {
      insuranceItems.splice(index, 1);
    } else {
      insuranceItems[index] = {
        ...item,
        operationType: 'Delete',
      };
    }

    updateCustomerDetailsState({ insurance: insuranceItems });
  };

  return (
    <Box class={classes.mainContainer}>
      <CollapsibleCard startTitle="Insurance" class="mb-2">
        <Box style={{ 'flex-grow': 1 }}>
          <Grid container spacing={1}>
            <Grid item size={{ xs: 12, lg: 4 }}>
              <TextField
                label="ATG Liability"
                placeholder="Enter ATG Liability"
                showInCurrencyFormat
                value={customerDetailsStore.customer.atgLiability ?? ''}
                onChange={(val: unknown) =>
                  updateCustomerDetailsState({ atgLiability: val as number })
                }
                class={classes.textFields}
                disabled={isReadonly()}
                type="number"
                maxLength={16}
                dataTestId="insurance-atg-liability-textInput"
              />
            </Grid>
            <Grid item size={{ xs: 12, lg: 4 }}>
              <TextField
                label="Agent Liability"
                placeholder="Enter Agent Liability"
                showInCurrencyFormat
                value={customerDetailsStore.customer.agentLiability ?? ''}
                onChange={(val: unknown) =>
                  updateCustomerDetailsState({ agentLiability: val as number })
                }
                class={classes.textFields}
                disabled={isReadonly()}
                type="number"
                maxLength={16}
                dataTestId="insurance-agent-liability-textInput"
              />
            </Grid>
            <Grid item size={{ xs: 12, lg: 4 }}>
              <DatePicker
                label="Agent Liability Effective Date"
                value={
                  customerDetailsStore.customer.agentLiabilityEffectiveDate ??
                  ''
                }
                handleChange={(date) =>
                  updateCustomerDetailsState({
                    agentLiabilityEffectiveDate: date,
                  })
                }
                class={classes.dateField}
                disabled={isReadonly()}
                dataTestId="insurance-agent-liability-effective-date-datePicker"
              />
            </Grid>
            <Grid item size={12} class={classes.messageTextArea}>
              <TextField
                multiline
                label="Agent Liability Note"
                rows={5}
                placeholder="Enter your note here."
                value={customerDetailsStore.customer.agentLiabilityNote ?? ''}
                onChange={(data) =>
                  updateCustomerDetailsState({
                    agentLiabilityNote: data,
                  })
                }
                disabled={isReadonly()}
                maxLength={500}
                dataTestId="insurance-agent-liability-note-textAreaField"
              />
            </Grid>
          </Grid>
        </Box>
        <Box class={classes.messageTextArea}>
          <div>
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((item) => (
                    <TableHeaderCell class="text-black!">
                      {item}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody class={classes.tableBg}>
                <Index each={customerDetailsStore.customer.insurance}>
                  {(item, index) => (
                    <Show when={item().operationType !== 'Delete'}>
                      <TableRow class={classes.tableRow}>
                        <TableCell>
                          <TextField
                            label=""
                            placeholder="Enter coverage amount"
                            size="sm"
                            class={classes.textFields}
                            showInCurrencyFormat
                            value={item().coverageAmount ?? '0'}
                            onChange={(val: unknown) =>
                              updateInsuranceItems(
                                'coverageAmount',
                                val as number,
                                index,
                              )
                            }
                            disabled={isReadonly()}
                            type="number"
                            maxLength={16}
                            dataTestId={`insurance-coverage-amount-textInput-${index}`}
                          />
                        </TableCell>
                        <TableCell>
                          <SelectInput
                            label=""
                            placeholder="Select Type"
                            value={item().policyType ?? ''}
                            menuItems={CustomerPolicyType.map((p) => ({
                              label: p.label,
                              value: String(p.value),
                            }))}
                            onChange={(val) => {
                              updateInsuranceItems('policyType', val, index);
                            }}
                            disabled={isReadonly()}
                            style={{
                              width: '100%',
                              height: '37px',
                            }}
                            dataTestId={formatDataTestId(
                              'Insurance policy type',
                              'selectInput',
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <DatePicker
                            value={
                              customerDetailsStore.customer.insurance?.[index]
                                .policyDate || DateTime.now().toISODate()
                            }
                            handleChange={(date: string) => {
                              updateInsuranceItems('policyDate', date, index);
                            }}
                            class={classes.dateField}
                            disabled={isReadonly()}
                            dataTestId={`insurance-policy-date-datePicker-${index}`}
                          />
                        </TableCell>
                        {!isReadonly() && (
                          <TableCell>
                            <IconButton
                              onClick={() => {
                                handleDeleteInsurance(index);
                              }}
                              disabled={isReadonly()}
                              dataTestId="customer-insurance-delete-icon-button"
                            >
                              <DeleteIcon class={classes.deleteIcon} />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    </Show>
                  )}
                </Index>
              </TableBody>
              {!isReadonly() && (
                <TableFooter class={classes.tableFooter}>
                  <TableRow>
                    <TableCell class="flex items-center">
                      <PlusCircleOutlineIcon
                        class={classes.addIcon}
                        onClick={addRow}
                      />
                      <span
                        class="text-[#1B4960]! font-normal! text-sm! ml-2 cursor-pointer"
                        onClick={addRow}
                      >
                        Add Insurance
                      </span>
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </div>
        </Box>
      </CollapsibleCard>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen('deleteFinancialInsuranceItem')}
        closeDialogBox={(_reason) =>
          closeDialogBox('deleteFinancialInsuranceItem')
        }
        dataTestId="deleteFinancialInsuranceItem"
        id="deleteFinancialInsuranceItem"
        title={'Are you sure you want to delete this line item?'}
        onSubmitText="Delete"
        onSubmit={() => {
          // TODO add functionality to remove the row from the table using operationType
          // removeTableRow(deleteRow()!, '');
          setDeleteRow(null);
        }}
      />
    </Box>
  );
};

export default Insurance;
